import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import { authService } from '../../../shared/lib/auth';

export default function ChangePasswordPage() {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();

  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [showPw, setShowPw] = useState({ current: false, new: false, confirm: false });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.currentPassword) e.currentPassword = 'Senha atual é obrigatória';
    if (!form.newPassword) {
      e.newPassword = 'Nova senha é obrigatória';
    } else if (form.newPassword.length < 8) {
      e.newPassword = 'Nova senha deve ter pelo menos 8 caracteres';
    } else if (!/(?=.*[A-Z])(?=.*\d)/.test(form.newPassword)) {
      e.newPassword = 'Nova senha deve conter pelo menos uma letra maiúscula e um número';
    }
    if (!form.confirmPassword) {
      e.confirmPassword = 'Confirmação de senha é obrigatória';
    } else if (form.newPassword !== form.confirmPassword) {
      e.confirmPassword = 'Senhas não conferem';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsLoading(true);
    try {
      await authService.changePassword(user?.email ?? '', form.currentPassword, form.newPassword);
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      setErrors({ submit: err?.message || 'Erro ao alterar senha. Tente novamente.' });
    } finally {
      setIsLoading(false);
    }
  };

  const getStrength = (pw: string) => {
    if (!pw) return { score: 0, text: '', color: '' };
    let s = 0;
    if (pw.length >= 8) s++;
    if (/[A-Z]/.test(pw)) s++;
    if (/[a-z]/.test(pw)) s++;
    if (/[0-9]/.test(pw)) s++;
    if (/[^A-Za-z0-9]/.test(pw)) s++;
    return s <= 2
      ? { score: s, text: 'Fraca', color: 'text-red-400' }
      : s <= 3
      ? { score: s, text: 'Média', color: 'text-yellow-400' }
      : { score: s, text: 'Forte', color: 'text-green-400' };
  };

  const strength = getStrength(form.newPassword);

  const toggle = (field: 'current' | 'new' | 'confirm') => (
    <button type="button" onClick={() => setShowPw(p => ({ ...p, [field]: !p[field] }))} className="text-slate-400 hover:text-slate-200">
      {showPw[field] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
    </button>
  );

  const field = (
    label: string,
    key: 'currentPassword' | 'newPassword' | 'confirmPassword',
    showKey: 'current' | 'new' | 'confirm',
    autocomplete: string,
  ) => (
    <div>
      <label className="block text-sm font-medium text-slate-300 mb-1">{label}</label>
      <div className="relative">
        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          type={showPw[showKey] ? 'text' : 'password'}
          autoComplete={autocomplete}
          value={form[key]}
          onChange={e => {
            setForm(p => ({ ...p, [key]: e.target.value }));
            if (errors[key]) setErrors(p => ({ ...p, [key]: '' }));
          }}
          className={`w-full bg-slate-900 border ${errors[key] ? 'border-red-500' : 'border-slate-600'} rounded-lg pl-10 pr-10 py-2.5 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm`}
          placeholder={label}
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2">{toggle(showKey)}</span>
      </div>
      {errors[key] && <p className="text-xs text-red-400 mt-1">{errors[key]}</p>}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <span className="text-3xl font-bold text-blue-500 tracking-tight">nexos</span>
          <p className="text-slate-400 mt-2 text-sm">Troca de senha obrigatória</p>
        </div>

        {/* Card */}
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-8 shadow-2xl">
          <div className="mb-6">
            <h1 className="text-xl font-semibold text-slate-100">Crie sua nova senha</h1>
            <p className="text-slate-400 text-sm mt-1">
              Você está usando uma senha temporária. Defina uma senha permanente para continuar.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {field('Senha atual (temporária)', 'currentPassword', 'current', 'current-password')}
            {field('Nova senha', 'newPassword', 'new', 'new-password')}

            {form.newPassword && (
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-slate-500">Força da senha</span>
                  <span className={strength.color}>{strength.text}</span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-1.5">
                  <div className={`h-1.5 rounded-full transition-all duration-300 ${
                    strength.score <= 2 ? 'bg-red-500 w-1/3' :
                    strength.score <= 3 ? 'bg-yellow-500 w-2/3' : 'bg-green-500 w-full'
                  }`} />
                </div>
              </div>
            )}

            {field('Confirmar nova senha', 'confirmPassword', 'confirm', 'new-password')}

            {/* Requirements */}
            <div className="bg-slate-900 rounded-lg p-3 border border-slate-700">
              <p className="text-xs font-medium text-slate-400 mb-2">Requisitos</p>
              <ul className="space-y-1">
                {[
                  { met: form.newPassword.length >= 8, text: 'Mínimo 8 caracteres' },
                  { met: /[A-Z]/.test(form.newPassword), text: 'Uma letra maiúscula' },
                  { met: /[0-9]/.test(form.newPassword), text: 'Um número' },
                ].map(({ met, text }) => (
                  <li key={text} className="flex items-center gap-2 text-xs">
                    <CheckCircle className={`h-3 w-3 ${met ? 'text-green-400' : 'text-slate-600'}`} />
                    <span className={met ? 'text-slate-300' : 'text-slate-500'}>{text}</span>
                  </li>
                ))}
              </ul>
            </div>

            {errors.submit && (
              <div className="flex items-center gap-2 bg-red-950 border border-red-800 rounded-lg p-3">
                <AlertCircle className="h-4 w-4 text-red-400 shrink-0" />
                <p className="text-sm text-red-300">{errors.submit}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-2.5 rounded-lg transition-colors text-sm mt-2"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white border-t-transparent" />
                  Alterando...
                </span>
              ) : 'Alterar senha e entrar'}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-slate-600 mt-6">
          {user?.email}
        </p>
      </div>
    </div>
  );
}
